import type { SupplierOrderStatus } from '../supplierOrderStatuses';

/**
 * Karta ZD według makiety, etapy 2–5 („Zamówione”, „W drodze”, „Przyjęte”,
 * „Zakończone”): the task list of every detailed status, the action each task
 * owns and the one next status button (makieta `checklist()`, `primaryKey()`,
 * `nextKey()`). Stage 1 „Szkic” has its own list in `supplierOrderDraftStage.ts`.
 *
 * Statuses of a ZD are informational (decision 2026-09-24-wp3): the next button
 * only waits for the open tasks of the card, the header „Aktualizuj status”
 * goes around the list, and „Zakończone” is set only by the purchase invoice.
 *
 * Owner decision 2026-10-05: no PZ before the purchase invoice. The PZ is created
 * when the purchase invoice is approved („Utworzyć PZ automatycznie?”), so the card
 * has no „przyjmij (PZ)” action: the PZ task only reports what exists, and the
 * purchase invoice is the step that moves the goods into the warehouse.
 */
export type SupplierOrderStageActionKey = 'confirmBySupplier' | 'planTransport' | 'addInvoice';

export type SupplierOrderStageTaskKey =
  | 'sent'
  | 'confirmed'
  | 'confirmedDate'
  | 'transport'
  | 'transportUnknown'
  | 'transportFreightUnknown'
  | 'pz'
  | 'invoice'
  | 'invoiceDone';

/** Statuses the next-status button can set (the rest are set by the work itself). */
export type SupplierOrderNextStatus = Extract<SupplierOrderStatus, 'IN_PRODUCTION' | 'READY' | 'SHIPPED'>;

export interface SupplierOrderStageFacts {
  status: SupplierOrderStatus;
  /** Ready date the supplier confirmed (display text) — „Gotowość towaru”. */
  expectedDelivery?: string | null;
  /** Trucks planned for this ZD (undefined while loading; null only when the list failed). */
  transportCount: number | null | undefined;
  /** Any truck of this ZD has no known freight yet. */
  freightUnknown?: boolean;
  /** Numbers of the received PZ documents of this ZD (null while loading or failed). */
  pzNumbers: string[] | null;
  /** Approved purchase invoices of this ZD (null while loading, failed or not visible to the viewer). */
  invoiceNumbers: string[] | null;
  /** The purchase invoice covers the whole ZD (status „Zakończone”). */
  invoiceComplete?: boolean;
}

export interface SupplierOrderStageTask {
  key: SupplierOrderStageTaskKey;
  done: boolean;
  /** Information, never blocks (makieta `info`). */
  info?: boolean;
  /** The task's own action; on a done task it may be offered again as „Zmień …”. */
  action?: SupplierOrderStageActionKey;
  /** Interpolation values of the task text. */
  values?: Record<string, string>;
}

export interface SupplierOrderStageModel {
  tasks: SupplierOrderStageTask[];
  /** Action of the first open task that has one (makieta `primaryKey()`). */
  primaryAction: SupplierOrderStageActionKey | null;
  /** The status the next button sets, null when the work itself moves the status on. */
  nextStatus: SupplierOrderNextStatus | null;
  /** Open tasks (not information) — keep the next button disabled. */
  blocking: SupplierOrderStageTaskKey[];
  /** Key of the help text under the stage title. */
  helpKey: string;
}

// From „W drodze” on only the purchase invoice moves the ZD (PZ and „Zakończone” follow from it).
const NEXT_STATUS: Partial<Record<SupplierOrderStatus, SupplierOrderNextStatus>> = {
  CONFIRMED: 'IN_PRODUCTION',
  IN_PRODUCTION: 'READY',
  READY: 'SHIPPED',
};

/**
 * Clicking a finished stage on the rail goes back to it (owner 2026-10-05: „klikamy po
 * prostu na kroki”; statuses are informational, any move is allowed): the status the ZD had
 * when that stage was done — the last status of the stage.
 */
export const STAGE_RETURN_STATUS: ReadonlyArray<SupplierOrderStatus> = ['DRAFT', 'CONFIRMED', 'SHIPPED', 'RECEIVED'];

const HELP_BY_STATUS: Partial<Record<SupplierOrderStatus, string>> = {
  SENT: 'sent',
  CONFIRMED: 'confirmed',
  IN_PRODUCTION: 'inProduction',
  READY: 'ready',
  SHIPPED: 'shipped',
  PARTIALLY_RECEIVED: 'partiallyReceived',
  RECEIVED: 'received',
  INVOICED: 'invoiced',
};

const joinNumbers = (numbers: string[] | null): string => (numbers && numbers.length ? numbers.join(', ') : '');

export function buildSupplierOrderStageModel(facts: SupplierOrderStageFacts): SupplierOrderStageModel {
  const { status } = facts;
  const tasks: SupplierOrderStageTask[] = [];
  // null = the list of trucks could not be read: unknown is not „none” — it neither blocks nor locks.
  const transportKnown = facts.transportCount !== null;
  const transportDone = (facts.transportCount ?? 0) > 0;
  const transportTask = (): SupplierOrderStageTask => (transportKnown
    ? { key: 'transport', done: transportDone, action: 'planTransport' }
    : { key: 'transportUnknown', done: false, info: true, action: 'planTransport' });
  const pzDone = (facts.pzNumbers?.length ?? 0) > 0;
  const invoiceComplete = Boolean(facts.invoiceComplete) || status === 'INVOICED';
  const pzValues = { numbers: joinNumbers(facts.pzNumbers) };
  const invoiceValues = { numbers: joinNumbers(facts.invoiceNumbers) };
  // The PZ comes from the approved purchase invoice: information, never a task of its own.
  const pzTask = (): SupplierOrderStageTask => ({ key: 'pz', done: pzDone, info: true, values: pzValues });
  const invoiceTask = (): SupplierOrderStageTask => (invoiceComplete
    ? { key: 'invoiceDone', done: true, values: invoiceValues }
    : { key: 'invoice', done: false, action: 'addInvoice', values: invoiceValues });

  if (status === 'SENT') {
    tasks.push({ key: 'sent', done: true });
    tasks.push({ key: 'confirmed', done: false, action: 'confirmBySupplier' });
  } else if (status === 'CONFIRMED') {
    tasks.push({ key: 'confirmedDate', done: true, values: { date: facts.expectedDelivery ?? '' } });
  } else if (status === 'IN_PRODUCTION' || status === 'READY') {
    if (facts.transportCount !== undefined) tasks.push(transportTask());
    if (transportDone && facts.freightUnknown) tasks.push({ key: 'transportFreightUnknown', done: true, info: true });
  } else if (status === 'SHIPPED') {
    // Owner 2026-10-14: no truck must not block the invoice/PZ; receipt shows the freight warning.
    if (facts.transportCount !== undefined) tasks.push(transportTask());
    if (transportDone && facts.freightUnknown) tasks.push({ key: 'transportFreightUnknown', done: true, info: true });
    tasks.push(invoiceTask());
    tasks.push(pzTask());
  } else if (status === 'PARTIALLY_RECEIVED' || status === 'RECEIVED') {
    tasks.push(invoiceTask());
    tasks.push(pzTask());
  } else if (status === 'INVOICED') {
    tasks.push({ key: 'invoiceDone', done: true, values: invoiceValues });
    tasks.push({ key: 'pz', done: true, info: true, values: pzValues });
  }

  const blocking = tasks.filter((task) => !task.done && !task.info).map((task) => task.key);
  const firstOpen = tasks.find((task) => !task.done && !task.info && task.action);

  return {
    tasks,
    primaryAction: firstOpen?.action ?? null,
    nextStatus: NEXT_STATUS[status] ?? null,
    blocking,
    helpKey: `orders:supplierOrders.card.help.${HELP_BY_STATUS[status] ?? 'sent'}`,
  };
}
